import React, { useState } from 'react';
import { Group, Button, Badge, Box } from '@mantine/core';
import useConfirmAction from '../../hooks/useConfirmAction.tsx';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import {
  convertToReportPeriodType,
  CronSettings,
  ParkingBillingSetting,
} from '../../models/parkingBillingSetting.ts';
import { weekDays } from '../../utils/BillingUtil.ts';
import useVoidParkingBillSetting from '../../hooks/useVoidParkingBillSetting.tsx';
import { notifications } from '@mantine/notifications';
import useLaunchParkingBillingSetting from '../../hooks/useLaunchParkingBillingSetting.ts';
import { PeriodOptionDescriptions } from '../../pages/BillingSettingsPageModel';
import { useDisclosure } from '@mantine/hooks';
import { LaunchParkingBillingModal } from '../modal/LaunchParkingBillingModal.tsx';

type BillingSettingsTableProps = {
  data: ParkingBillingSetting[];
  isLoading: boolean;
  onEdit: (id: string) => void;
};

export default function BillingSettingsTable({
  data = [],
  isLoading,
  onEdit,
}: BillingSettingsTableProps) {
  const confirmAction = useConfirmAction();
  const { mutate: voidSetting } = useVoidParkingBillSetting();
  const { mutate: launchBilling, isPending: isLaunchBillingPending } =
    useLaunchParkingBillingSetting();

  const [selectedBillingSetting, setSelectedBillingSetting] =
    useState<ParkingBillingSetting | null>(null);
  const [
    billingModalOpened,
    { open: openBillingModal, close: closeBillingModal },
  ] = useDisclosure(false);

  const executionDisplay = (settings: CronSettings) => {
    const { hour, monthday, weekday } = settings;
    if (monthday !== '*') {
      return `每月 ${monthday} 日 ${hour} 點`;
    } else {
      return `每週${weekDays.find((day) => day.value === weekday)?.label} ${hour} 點`;
    }
  };

  const handleBillingConfirm = ({
    offset,
    accountantIds,
    datePeriod,
  }: {
    offset: number;
    accountantIds: string[];
    datePeriod: string;
  }) => {
    launchBilling(
      {
        id: selectedBillingSetting.id,
        offset: offset > 0 ? -offset : offset,
        accountantIds,
      },
      {
        onSuccess: () => {
          notifications.show({
            title: '成功',
            message: `對帳設定 ${selectedBillingSetting.name}(${datePeriod}) 已觸發`,
            color: 'green',
          });
          closeBillingModal();
        },
      },
    );
  };

  const columns: DataTableColumn<ParkingBillingSetting>[] = [
    { title: '對帳單名稱', accessor: 'name' },
    {
      title: '廠商',
      accessor: 'siteProvider',
      render: ({ siteProvider }) => siteProvider.name,
    },
    {
      title: '執行週期',
      accessor: 'executionPeriod',
      render: (row) => executionDisplay(row.scheduledTaskTriggerRule.settings),
    },
    {
      title: '報表週期',
      accessor: 'reportPeriod',
      render: ({ args }) => {
        const reportPeriodType = convertToReportPeriodType(args.reportPeriod);
        const description = PeriodOptionDescriptions[reportPeriodType];
        switch (reportPeriodType) {
          case 'week':
            return `每週（${description}）`;
          case 'month':
            return `每月（${description}）`;
          case 'firstHalfOfMonth':
            return `上半月（${description}）`;
          case 'secondHalfOfMonth':
            return `下半月（${description}）`;
        }
        return '未知';
      },
    },
    {
      title: '標籤',
      accessor: 'tags',
      render: (row) => (
        <Group>
          {row.args.billingTags?.map((tag) => (
            <Badge key={tag} variant="outline">
              {tag}
            </Badge>
          ))}
        </Group>
      ),
    },
    {
      title: '操作',
      accessor: 'actions',
      render: (row) => (
        <Group>
          <Button size="xs" onClick={() => onEdit(row.id)}>
            編輯
          </Button>
          <Button
            size="xs"
            color="blue"
            onClick={() => {
              setSelectedBillingSetting(row);
              openBillingModal();
            }}
          >
            重產對帳單
          </Button>
          <Button
            size="xs"
            color="red"
            variant="outline"
            onClick={() =>
              confirmAction({
                title: `作廢設定 ${row.name}`,
                onConfirmAction: () => {
                  voidSetting(row.id, {
                    onSuccess: () => {
                      notifications.show({
                        title: '成功',
                        message: `對帳設定 ${row.name} 已作廢`,
                        color: 'green',
                      });
                    },
                  });
                },
              })
            }
          >
            刪除
          </Button>
        </Group>
      ),
    },
  ];

  return (
    <>
      {selectedBillingSetting && billingModalOpened && (
        <LaunchParkingBillingModal
          opened={billingModalOpened}
          onClose={closeBillingModal}
          parkingBillingSetting={selectedBillingSetting}
          loading={isLaunchBillingPending}
          onConfirm={handleBillingConfirm}
        />
      )}
      <Box>
        <DataTable
          idAccessor="id"
          highlightOnHover
          minHeight={150}
          columns={columns}
          records={data}
          verticalSpacing="xs"
          fetching={isLoading}
          noRecordsText="沒有歷史對帳單"
        />
      </Box>
    </>
  );
}
